import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminSidebar from '../../../components/admin/AdminSidebar';
import AdminHeader from '../../../components/admin/AdminHeader';
import SiteForm from '../../../components/admin/SiteForm';
import { buildApiUrl, toUploadUrl } from '../../../config/api';

const AdminCampingSites = () => {
    const [sites, setSites] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingSite, setEditingSite] = useState(null);
    const [filters, setFilters] = useState({
        location: '',
        site_type: '',
        status: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);

    const getToken = () => localStorage.getItem('token');

    const fetchSites = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = getToken();
            const params = new URLSearchParams();
            if (filters.location) params.append('location', filters.location);
            if (filters.site_type) params.append('site_type', filters.site_type);
            if (filters.status) params.append('status', filters.status);
            
            const url = `${buildApiUrl('/camping/sites')}?${params.toString()}`;
            console.log('📡 Fetching from:', url);
            
            const response = await axios.get(url, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (response.data.success) {
                setSites(response.data.data || []);
                console.log('✅ Sites loaded:', response.data.data?.length);
            } else {
                setSites([]);
            }
        } catch (error) {
            console.error('❌ Error fetching sites:', error);
            if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
                setTimeout(() => {
                    localStorage.removeItem('token');
                    window.location.href = '/admin/login';
                }, 2000);
            } else {
                setError(error.response?.data?.message || 'Failed to load camping sites');
            }
            setSites([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchLocations = useCallback(async () => {
        try {
            const token = getToken();
            const response = await axios.get(buildApiUrl('/camping/locations'), {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (response.data.success) {
                setLocations(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    }, []);

    useEffect(() => {
        fetchSites();
        fetchLocations();
    }, [fetchLocations, fetchSites]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this camping site?')) return;
        
        try {
            const token = getToken();
            if (!token) {
                alert('Please login to perform this action');
                window.location.href = '/admin/login';
                return;
            }
            
            await axios.delete(buildApiUrl(`/camping/sites/${id}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            alert('✅ Site deleted successfully');
            fetchSites();
        } catch (error) {
            console.error('Error deleting site:', error);
            alert(error.response?.data?.message || 'Failed to delete site');
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            const token = getToken();
            if (!token) {
                alert('Please login to perform this action');
                window.location.href = '/admin/login';
                return;
            }
            
            await axios.patch(
                buildApiUrl(`/camping/sites/${id}/status`),
                { status },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            alert('✅ Status updated successfully');
            fetchSites();
        } catch (error) {
            console.error('Error updating status:', error);
            alert(error.response?.data?.message || 'Failed to update status');
        }
    };

    // ✅ Improved image URL handler
    const getImageUrl = (imagePath) => {
        if (!imagePath) {
            return 'https://placehold.co/300x200/1a1a1a/ffffff?text=No+Image';
        }
        
        // If already full URL
        if (imagePath.startsWith('http')) {
            return imagePath;
        }
        
        // Remove any leading slashes
        let cleanPath = imagePath.replace(/^\/+/, '');
        
        // If path already includes uploads, don't duplicate
        if (cleanPath.startsWith('uploads/')) {
            return toUploadUrl(cleanPath);
        }
        
        // Default: add uploads folder
        return toUploadUrl(cleanPath, { camping: true });
    };

    const getStatusBadgeClass = (status) => {
        const classes = {
            'available': 'badge-success',
            'booked': 'badge-warning',
            'maintenance': 'badge-danger'
        };
        return classes[status] || 'badge-secondary';
    };

    const filteredSites = sites.filter(site => 
        site.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.location_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.site_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const siteTypes = ['tent', 'cabin', 'glamping', 'rv', 'campsite'];

    const clearFilters = () => {
        setFilters({ location: '', site_type: '', status: '' });
        setSearchTerm('');
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader title="Camping Sites Management" />
                
                <div className="admin-camping-sites">
                    <div className="section-header">
                        <h2>
                            <i className="fas fa-campground"></i>
                            Camping Sites
                            <span className="site-count">({filteredSites.length})</span>
                        </h2>
                        <div className="header-actions">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by name, location or type..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button className="clear-search" onClick={() => setSearchTerm('')}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                            <button 
                                className="btn-primary"
                                onClick={() => {
                                    setEditingSite(null);
                                    setShowForm(true);
                                }}
                            >
                                <i className="fas fa-plus"></i> Add New Site
                            </button>
                        </div>
                    </div>

                    <div className="filters-bar">
                        <select 
                            value={filters.location} 
                            onChange={(e) => setFilters({...filters, location: e.target.value})}
                            className="filter-select"
                        >
                            <option value="">All Locations</option>
                            {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>

                        <select 
                            value={filters.site_type} 
                            onChange={(e) => setFilters({...filters, site_type: e.target.value})}
                            className="filter-select"
                        >
                            <option value="">All Types</option>
                            {siteTypes.map(type => (
                                <option key={type} value={type}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </option>
                            ))}
                        </select>

                        <select 
                            value={filters.status} 
                            onChange={(e) => setFilters({...filters, status: e.target.value})}
                            className="filter-select"
                        >
                            <option value="">All Status</option>
                            <option value="available">Available</option>
                            <option value="booked">Booked</option>
                            <option value="maintenance">Maintenance</option>
                        </select>

                        {(filters.location || filters.site_type || filters.status) && (
                            <button className="btn-clear-filters" onClick={clearFilters}>
                                <i className="fas fa-times"></i> Clear Filters
                            </button>
                        )}
                    </div>

                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            <span>{error}</span>
                            <button onClick={fetchSites} className="retry-btn">
                                <i className="fas fa-sync-alt"></i> Retry
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading camping sites...</p>
                        </div>
                    ) : (
                        <>
                            {filteredSites.length === 0 ? (
                                <div className="no-results">
                                    <i className="fas fa-campground"></i>
                                    <h3>No camping sites found</h3>
                                    <p>Try adjusting your filters or add a new site</p>
                                    <button 
                                        className="btn-primary"
                                        onClick={() => {
                                            setEditingSite(null);
                                            setShowForm(true);
                                        }}
                                    >
                                        <i className="fas fa-plus"></i> Add New Site
                                    </button>
                                </div>
                            ) : (
                                <div className="sites-grid">
                                    {filteredSites.map(site => (
                                        <div key={site.id} className="site-card">
                                            <div className="site-image">
                                                <img 
                                                    src={getImageUrl(site.image)} 
                                                    alt={site.name}
                                                    onError={(e) => {
                                                        console.log('Image load error:', site.image);
                                                        e.target.src = 'https://placehold.co/300x200/1a1a1a/ffffff?text=No+Image';
                                                    }}
                                                />
                                                <span className={`status-badge ${getStatusBadgeClass(site.status)}`}>
                                                    {site.status}
                                                </span>
                                                {site.discount_price && (
                                                    <span className="discount-badge">
                                                        -{Math.round((1 - site.discount_price / site.price_per_night) * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                            <div className="site-info">
                                                <h3>{site.name}</h3>
                                                <p className="location">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                    {site.location_name}
                                                </p>
                                                <div className="site-meta">
                                                    <span className="type-badge">{site.site_type}</span>
                                                    <span className="capacity">
                                                        <i className="fas fa-users"></i> {site.capacity} {site.capacity === 1 ? 'person' : 'persons'}
                                                    </span>
                                                    <span className="price">
                                                        ₹{parseFloat(site.price_per_night).toLocaleString('en-IN')}/night
                                                    </span>
                                                </div>
                                                
                                                <div className="amenities-preview">
                                                    {site.has_electricity === 1 && (
                                                        <span title="Electricity"><i className="fas fa-bolt"></i></span>
                                                    )}
                                                    {site.has_water === 1 && (
                                                        <span title="Water"><i className="fas fa-water"></i></span>
                                                    )}
                                                    {site.has_wifi === 1 && (
                                                        <span title="WiFi"><i className="fas fa-wifi"></i></span>
                                                    )}
                                                    {site.has_fire_pit === 1 && (
                                                        <span title="Fire Pit"><i className="fas fa-fire"></i></span>
                                                    )}
                                                    {site.pet_friendly === 1 && (
                                                        <span title="Pet Friendly"><i className="fas fa-paw"></i></span>
                                                    )}
                                                </div>
                                                
                                                <div className="site-actions">
                                                    <select 
                                                        className={`status-select ${site.status}`}
                                                        value={site.status}
                                                        onChange={(e) => handleStatusChange(site.id, e.target.value)}
                                                    >
                                                        <option value="available">Available</option>
                                                        <option value="booked">Booked</option>
                                                        <option value="maintenance">Maintenance</option>
                                                    </select>
                                                    <button 
                                                        className="btn-icon edit"
                                                        onClick={() => {
                                                            setEditingSite(site);
                                                            setShowForm(true);
                                                        }}
                                                        title="Edit Site"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button 
                                                        className="btn-icon delete"
                                                        onClick={() => handleDelete(site.id)}
                                                        title="Delete Site"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {showForm && (
                        <SiteForm
                            site={editingSite}
                            locations={locations}
                            onClose={() => {
                                setShowForm(false);
                                setEditingSite(null);
                            }}
                            onSuccess={() => {
                                fetchSites();
                                setShowForm(false);
                                setEditingSite(null);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminCampingSites;
